import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI, intencionesAPI } from "@/lib/api";
import { useAuth } from "@/App";
import {
  Droplets,
  HeartHandshake,
  Flame,
  Users,
  CalendarDays,
  Plus,
  ArrowRight,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [intenciones, setIntenciones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, intencionesRes] = await Promise.all([
        dashboardAPI.getStats(),
        intencionesAPI.getAll({ celebrada: false }),
      ]);
      setStats(statsRes.data);
      setIntenciones(intencionesRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Bautizos",
      value: stats?.bautizos || 0,
      icon: Droplets,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      link: "/bautizos",
    },
    {
      title: "Matrimonios",
      value: stats?.matrimonios || 0,
      icon: HeartHandshake,
      color: "text-rose-600",
      bgColor: "bg-rose-50",
      link: "/matrimonios",
    },
    {
      title: "Confirmaciones",
      value: stats?.confirmaciones || 0,
      icon: Flame,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      link: "/confirmaciones",
    },
    {
      title: "Feligreses",
      value: stats?.personas || 0,
      icon: Users,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      link: "/personas",
    },
  ];

  const quickActions = [
    { label: "Nuevo Bautizo", link: "/bautizos/nuevo", icon: Droplets },
    { label: "Nuevo Matrimonio", link: "/matrimonios/nuevo", icon: HeartHandshake },
    { label: "Nueva Confirmación", link: "/confirmaciones/nuevo", icon: Flame },
    { label: "Nueva Intención", link: "/intenciones", icon: CalendarDays },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-serif text-rose-950 tracking-tight">
          Bienvenido, {user?.nombre}
        </h1>
        <p className="text-stone-600 mt-2">Panel de administración parroquial</p>
      </div>

      {/* Stats Grid - Bento style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <Card className="card-hover border-stone-200 bg-white h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-stone-500 uppercase tracking-wide">
                        {stat.title}
                      </p>
                      <p className="text-3xl font-serif text-stone-800 mt-2">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-sm text-stone-500 hover:text-rose-900">
                    <span>Ver registros</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="border-stone-200 bg-white">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-serif text-stone-800">
              Acciones Rápidas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link key={action.label} to={action.link}>
                  <Button
                    variant="outline"
                    className="w-full justify-start h-12 text-stone-700 border-stone-200 hover:bg-rose-50 hover:text-rose-900 hover:border-rose-200"
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <Icon className="w-5 h-5 mr-3" strokeWidth={1.5} />
                    {action.label}
                    <Plus className="w-4 h-4 ml-auto" />
                  </Button>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        {/* Upcoming Mass Intentions */}
        <Card className="lg:col-span-2 border-stone-200 bg-white">
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-serif text-stone-800">
              Intenciones Próximas
            </CardTitle>
            <Link to="/intenciones">
              <Button variant="ghost" size="sm" className="text-rose-900">
                Ver todas
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {intenciones.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay intenciones pendientes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {intenciones.map((intencion) => (
                  <div
                    key={intencion.id}
                    className="flex items-start gap-4 p-4 rounded-lg bg-stone-50 border border-stone-100"
                  >
                    <div className="p-2 rounded-md bg-amber-100">
                      <Clock className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">
                        {intencion.intencion}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        {intencion.fecha_misa} · {intencion.hora_misa}
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        Solicitante: {intencion.solicitante_nombre}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0">
                      Pendiente
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info banner */}
      <Card className="border-rose-200 bg-rose-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-rose-100">
              <CalendarDays className="w-6 h-6 text-rose-700" />
            </div>
            <div>
              <h3 className="font-medium text-rose-900">
                {stats?.intenciones_pendientes || 0} intenciones pendientes
              </h3>
              <p className="text-sm text-rose-700">
                Recuerda revisar las intenciones antes de cada celebración
              </p>
            </div>
            <Link to="/intenciones" className="ml-auto">
              <Button className="bg-rose-950 hover:bg-rose-900 text-white">
                Gestionar Intenciones
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
